import React from "react";
import { useRouter } from "next/router";
import { PACKAGE_COLORS } from "../../data/diagnosticQuestions";
import styles from "./TrialWelcomeModal.module.scss";

const PACKAGE_NAMES = {
    kveikja: "Kveikja",
    vaxa: "Vaxa",
    efla: "Efla",
    dafna: "Dafna",
};

const HIGHLIGHTS_IS = {
    kveikja: [
        "120+ aðgerðir",
        "Kolefnisreiknir",
        "Markmið og áætlun",
        "Fullt mælaborð",
        "Laufaskólinn",
    ],
    vaxa: [
        "Birgjamat",
        "Fjármálayfirlit",
        "Skýrslur og útflutningur",
        "Starfsmannakannanir",
        "Samanburður",
    ],
    efla: [
        "Kolka raungögn",
        "Eigna-app",
        "HR mælaborð",
        "Ítarlegur kolefnisreikningur",
        "Sérstakur tengiliður",
    ],
    dafna: [
        "Gæðastjórnunarkerfi",
        "Eignastjórnun",
        "Kolefnisbókhald",
        "Stofnanastjórnun",
        "Heimsmarkmiðin",
    ],
};

const HIGHLIGHTS_EN = {
    kveikja: [
        "120+ actions",
        "Carbon calculator",
        "Goals & plan",
        "Full dashboard",
        "Laufaskólinn",
    ],
    vaxa: [
        "Supplier assessment",
        "Financial overview",
        "Reports & export",
        "Employee surveys",
        "Benchmarking",
    ],
    efla: [
        "Kolka live data",
        "Property app",
        "HR dashboard",
        "Detailed carbon calc",
        "Dedicated contact",
    ],
    dafna: [
        "Quality management",
        "Asset management",
        "Carbon accounting",
        "Multi-entity",
        "UN SDGs",
    ],
};

export default function TrialWelcomeModal({
    recommendation,
    contactName,
    contactEmail,
    onClose,
    onBookMeeting,
}) {
    const { locale } = useRouter();
    const isEn = locale === "en";
    const color = PACKAGE_COLORS[recommendation] || PACKAGE_COLORS.none;
    const pkgName = PACKAGE_NAMES[recommendation];
    const isDafna = recommendation === "dafna";
    const isNone = recommendation === "none";

    const highlights = isEn
        ? HIGHLIGHTS_EN[recommendation] || []
        : HIGHLIGHTS_IS[recommendation] || [];

    const registrationUrl = isNone
        ? "https://admin.laufid.is/registration"
        : `https://admin.laufid.is/registration?tier=${recommendation}${contactName ? `&name=${encodeURIComponent(contactName)}` : ""}${contactEmail ? `&email=${encodeURIComponent(contactEmail)}` : ""}`;

    const firstName = contactName ? contactName.split(" ")[0] : "";

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    if (isNone) {
        return (
            <div className={styles.overlay} onClick={handleOverlayClick}>
                <div className={styles.modal}>
                    <button
                        className={styles.closeBtn}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>

                    <div className={styles.greeting}>
                        {firstName &&
                            (isEn ? `Hi ${firstName}!` : `Hæ ${firstName}!`)}
                    </div>

                    <h2 className={styles.headline}>
                        {isEn
                            ? "Start free — explore at your own pace"
                            : "Byrjaðu frítt — skoðaðu í þínum tíma"}
                    </h2>

                    <p className={styles.subtext}>
                        {isEn
                            ? "Laufið is free to start with 40 green steps and full access to Laufaskólinn. No credit card needed."
                            : "Laufið er frítt til að byrja með 40 græn skref og fullan aðgang að Laufaskólanum. Ekkert greiðslukort þarf."}
                    </p>

                    <div className={styles.actions}>
                        <a
                            href={registrationUrl}
                            className={styles.primaryBtn}
                            style={{ backgroundColor: "#009448" }}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {isEn
                                ? "Create free account →"
                                : "Stofna frítt aðgang →"}
                        </a>
                        <button
                            className={styles.secondaryBtn}
                            onClick={onBookMeeting}
                        >
                            {isEn
                                ? "Book a short intro call"
                                : "Bóka stuttan kynningu-fund"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.modal}>
                <button
                    className={styles.closeBtn}
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>

                <div
                    className={styles.badge}
                    style={{
                        backgroundColor: `${color}12`,
                        color,
                        border: `1.5px solid ${color}`,
                    }}
                >
                    {pkgName}
                </div>

                {firstName && (
                    <div className={styles.greeting}>
                        {isEn ? `Hi ${firstName}!` : `Hæ ${firstName}!`}
                    </div>
                )}

                <h2 className={styles.headline} style={{ color }}>
                    {isEn
                        ? `Try ${pkgName} free for 14 days`
                        : `Prófaðu ${pkgName} frítt í 14 daga`}
                </h2>

                <p className={styles.subtext}>
                    {isDafna
                        ? isEn
                            ? "Dafna is our most comprehensive solution. Sign up and start exploring right away — we'll book a short setup call to activate Kolka live data and walk you through the system."
                            : "Dafna er heildstæðasta lausn okkar. Skráðu þig og byrjaðu strax — við bókum svo stuttan uppsetningarfund til að virkja Kolka raungögnin og sýna þér kerfið."
                        : recommendation === "efla"
                          ? isEn
                              ? `Get full access to ${pkgName} with no obligations. Kolka live data is activated via a short setup call after sign-up. Cancel anytime.`
                              : `Fáðu fullan aðgang að ${pkgName} án skuldbindinga. Kolka raungögnin eru virkjuð með stuttum uppsetningarfundi eftir skráningu. Hættu hvenær sem er.`
                          : isEn
                            ? `Get full access to ${pkgName} with no obligations. Cancel anytime.`
                            : `Fáðu fullan aðgang að ${pkgName} án skuldbindinga. Hættu hvenær sem er.`}
                </p>

                {highlights.length > 0 && (
                    <div className={styles.features}>
                        {highlights.map((h, i) => (
                            <div key={i} className={styles.featureItem}>
                                <span
                                    className={styles.featureCheck}
                                    style={{ color }}
                                >
                                    ✓
                                </span>
                                {h}
                            </div>
                        ))}
                    </div>
                )}

                <div className={styles.actions}>
                    <a
                        href={registrationUrl}
                        className={styles.primaryBtn}
                        style={{ backgroundColor: color }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {isEn
                            ? "Sign up & start (14 days free) →"
                            : "Skrá mig og byrja (14 daga prufa) →"}
                    </a>
                    <button
                        className={styles.secondaryBtn}
                        onClick={onBookMeeting}
                    >
                        {isDafna || recommendation === "efla"
                            ? isEn
                                ? "Book a setup call instead"
                                : "Bóka uppsetningarfund í staðinn"
                            : isEn
                              ? "Book a short intro call first"
                              : "Bóka stuttan kynningu-fund fyrst"}
                    </button>
                </div>

                <p className={styles.freeNote}>
                    {isEn
                        ? "No credit card required. Cancel anytime."
                        : "Ekkert greiðslukort nauðsynlegt. Hættu hvenær sem er."}
                </p>
            </div>
        </div>
    );
}
